import posthog from 'posthog-js';
import { AxiosError } from 'axios';

/**
 * Error severity levels for categorization and filtering in PostHog
 */
export type ErrorSeverity = 'fatal' | 'error' | 'warning' | 'info';

/**
 * Context information attached to error events
 */
export interface ErrorContext {
  /** Component or module where the error occurred */
  component?: string;
  /** Action being performed when error occurred */
  action?: string;
  /** Additional metadata for debugging */
  metadata?: Record<string, unknown>;
  /** Error severity level */
  severity?: ErrorSeverity;
  /** User-facing error message (if different from technical message) */
  userMessage?: string;
}

/**
 * API error context for HTTP request failures
 */
export interface ApiErrorContext extends ErrorContext {
  /** Request URL */
  url?: string;
  /** HTTP method */
  method?: string;
  /** HTTP status code */
  statusCode?: number;
  /** Response data */
  responseData?: unknown;
}

/**
 * Extracts a clean error message from various error types
 */
function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'Unknown error occurred';
}

/**
 * Extracts stack trace if available
 */
function extractStackTrace(error: unknown): string | undefined {
  if (error instanceof Error) {
    return error.stack;
  }
  return undefined;
}

/**
 * Generates a fingerprint for error deduplication
 */
function generateErrorFingerprint(
  message: string,
  component?: string,
  action?: string
): string {
  const parts = [message, component, action].filter(Boolean);
  return parts.join('|');
}

/**
 * Captures and reports an error to PostHog
 *
 * @param error - The error to capture
 * @param context - Additional context about the error
 *
 * @example
 * ```ts
 * try {
 *   await someOperation();
 * } catch (error) {
 *   captureError(error, {
 *     component: 'UserProfile',
 *     action: 'fetchUserData',
 *     severity: 'error'
 *   });
 * }
 * ```
 */
export function captureError(error: unknown, context: ErrorContext = {}): void {
  const message = extractErrorMessage(error);
  const stack = extractStackTrace(error);
  const severity = context.severity ?? 'error';
  const fingerprint = generateErrorFingerprint(
    message,
    context.component,
    context.action
  );

  // Also log to console for development
  console.error(`[${severity.toUpperCase()}]`, message, context);

  posthog.capture('exception', {
    $exception_message: message,
    $exception_stack_trace_raw: stack,
    $exception_type: error instanceof Error ? error.name : 'Error',
    $exception_fingerprint: fingerprint,
    severity,
    component: context.component,
    action: context.action,
    user_message: context.userMessage,
    ...context.metadata,
  });
}

/**
 * Captures API/HTTP errors with request context
 *
 * @param error - The axios error or generic error
 * @param context - API-specific context
 *
 * @example
 * ```ts
 * try {
 *   await axios.get('/api/users');
 * } catch (error) {
 *   captureApiError(error, {
 *     component: 'UserService',
 *     action: 'getUsers'
 *   });
 * }
 * ```
 */
export function captureApiError(
  error: unknown,
  context: ApiErrorContext = {}
): void {
  let apiContext: ApiErrorContext = { ...context };

  // Extract axios-specific information
  if (error instanceof AxiosError) {
    apiContext = {
      ...apiContext,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      statusCode: error.response?.status,
      responseData: error.response?.data,
    };
  }

  const message = extractErrorMessage(error);
  const stack = extractStackTrace(error);
  const severity = context.severity ?? 'error';

  // Log to console for development
  console.error(
    `[API ${severity.toUpperCase()}]`,
    `${apiContext.method} ${apiContext.url}`,
    message,
    context
  );

  posthog.capture('api_error', {
    $exception_message: message,
    $exception_stack_trace_raw: stack,
    $exception_type: error instanceof Error ? error.name : 'ApiError',
    severity,
    url: apiContext.url,
    method: apiContext.method,
    status_code: apiContext.statusCode,
    response_data: apiContext.responseData,
    component: context.component,
    action: context.action,
    ...context.metadata,
  });
}

/**
 * Captures React component errors from ErrorBoundary
 *
 * @param error - The error thrown by the component
 * @param componentStack - React component stack trace
 * @param componentName - Name of the component that errored
 */
export function captureComponentError(
  error: Error,
  componentStack?: string,
  componentName?: string
): void {
  console.error('[COMPONENT ERROR]', error.message, componentStack);

  posthog.capture('exception', {
    $exception_message: error.message,
    $exception_stack_trace_raw: error.stack,
    $exception_type: error.name,
    severity: 'fatal',
    component: componentName ?? 'Unknown',
    react_component_stack: componentStack,
    is_react_error: true,
  });
}

/**
 * Captures unhandled promise rejections
 * Should be set up once at app initialization
 */
export function setupGlobalErrorHandlers(): void {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    captureError(event.reason, {
      component: 'Global',
      action: 'unhandledRejection',
      severity: 'error',
      metadata: {
        type: 'unhandled_promise_rejection',
      },
    });
  });

  // Handle uncaught errors
  window.addEventListener('error', (event) => {
    captureError(event.error ?? event.message, {
      component: 'Global',
      action: 'uncaughtError',
      severity: 'fatal',
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });
}
